import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import { useAuth } from '../../AuthContext';
import { User, Mail, Shield, Building2, Calendar, Save, Loader2, KeyRound, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import MfaSection from './MfaSection';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const initials = (name = '', email = '') => {
  const base = (name || email || '?').trim();
  if (!base) return '?';
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

export default function MyProfile() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  // Password panel state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdSavedAt, setPwdSavedAt] = useState(null);
  const hasPassword = Boolean(user?.has_password);

  if (!user) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="profile-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading profile…
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/me`, { name }, { withCredentials: true });
      toast.success('Profile updated');
      if (checkAuth) await checkAuth();
    } catch (e) {
      // The PATCH endpoint may not be implemented yet — fall back gracefully.
      if (e?.response?.status === 404 || e?.response?.status === 405) {
        toast.message('Profile editing is read-only for now. Email sales@cloudgenie.co to update.');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to save profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (newPwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    if (hasPassword && !currentPwd) { toast.error('Enter your current password'); return; }
    setSavingPwd(true);
    try {
      const payload = { new_password: newPwd };
      if (hasPassword) payload.current_password = currentPwd;
      const r = await axios.post(
        `${API}/auth/set-my-password`,
        payload,
        { withCredentials: true },
      );
      setPwdSavedAt(r.data?.rotated_at || new Date().toISOString());
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      toast.success(hasPassword ? 'Password updated' : 'Password set — you can now sign in with email + password');
      if (checkAuth) await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to set password');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="my-profile">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground flex items-center gap-2">
          <User className="h-6 w-6 text-blue-500" />
          My Profile
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your account details, signed-in user info and current organisation membership.
        </p>
      </div>

      {/* Identity card */}
      <Card className="p-6 flex items-center gap-5" data-testid="profile-identity">
        <div
          className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl font-semibold text-white shadow-lg"
          aria-label="avatar"
        >
          {user.avatar_url ? (
            <img src={user.avatar_url} alt="profile" className="h-full w-full rounded-full object-cover" />
          ) : (
            initials(user.name, user.email)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xl font-semibold text-foreground truncate">
            {user.name || user.email}
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
            <Mail className="h-3.5 w-3.5" />
            {user.email}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {user.role && (
              <Badge variant="outline" className="gap-1 border-amber-500/40 text-amber-300 bg-amber-500/5">
                <Shield className="h-3 w-3" />
                {user.role}
              </Badge>
            )}
            {user.plan && (
              <Badge variant="outline" className="gap-1 border-blue-500/40 text-blue-300 bg-blue-500/5">
                {user.plan}
              </Badge>
            )}
            {user.email_verified && (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
                Email verified
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Editable fields */}
      <Card className="p-6 space-y-4" data-testid="profile-edit">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Personal info
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="profile-name">Display name</Label>
            <Input
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="profile-name-input"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user.email || ''} disabled className="mt-1.5" />
            <p className="text-[11px] text-muted-foreground mt-1">
              Email is the unique identifier and cannot be changed here.
            </p>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving || name === (user.name || '')} data-testid="profile-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save changes
          </Button>
        </div>
      </Card>

      {/* Password card — first-time set OR rotate */}
      <Card className="p-6 space-y-4" data-testid="profile-password-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5" />
              {hasPassword ? 'Change password' : 'Add a password (backup login)'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-xl">
              {hasPassword
                ? 'Rotate your password. You will need your current password to confirm.'
                : `You signed in via ${user.auth_provider === 'password' ? 'username + password' : 'Google'}. Add a password so you can also sign in with email + password from any device.`}
            </p>
          </div>
          {hasPassword && (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/5 gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Password set
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {hasPassword && (
            <div>
              <Label htmlFor="current-pwd" className="text-xs">Current password</Label>
              <Input
                id="current-pwd"
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                className="mt-1.5"
                data-testid="profile-current-pwd"
              />
            </div>
          )}
          <div>
            <Label htmlFor="new-pwd" className="text-xs">New password (min 8 chars)</Label>
            <Input
              id="new-pwd"
              type={showPwd ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              className="mt-1.5"
              data-testid="profile-new-pwd"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pwd" className="text-xs">Confirm new password</Label>
            <Input
              id="confirm-pwd"
              type={showPwd ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              className="mt-1.5"
              data-testid="profile-confirm-pwd"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowPwd((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
            data-testid="profile-toggle-pwd-visibility"
          >
            {showPwd ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showPwd ? 'Hide' : 'Show'} passwords
          </button>
          <div className="flex items-center gap-2">
            {pwdSavedAt && (
              <span className="text-[11px] text-emerald-400">
                Saved {new Date(pwdSavedAt).toLocaleTimeString()}
              </span>
            )}
            <Button
              onClick={savePassword}
              disabled={savingPwd || !newPwd || newPwd !== confirmPwd}
              data-testid="profile-save-pwd"
            >
              {savingPwd
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <KeyRound className="h-4 w-4 mr-2" />}
              {hasPassword ? 'Update password' : 'Set password'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Two-factor authentication (admin / super_admin only) */}
      <MfaSection />

      {/* Membership info */}
      <Card className="p-6" data-testid="profile-membership">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
          Organisation & membership
        </h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Field label="Current org" icon={Building2}>
            {user.org_name || user.org_id || '—'}
          </Field>
          <Field label="Org ID" icon={Building2} mono>
            {user.org_id || '—'}
          </Field>
          <Field label="User ID" icon={User} mono>
            {user.user_id || user.id || '—'}
          </Field>
          <Field label="Account created" icon={Calendar}>
            {formatDate(user.created_at)}
          </Field>
          <Field label="Last login" icon={Calendar}>
            {formatDate(user.last_login || user.last_seen)}
          </Field>
          <Field label="Auth provider" icon={Shield}>
            {user.auth_provider || 'email'}
          </Field>
        </dl>
      </Card>
    </div>
  );
}

function Field({ label, icon: Icon, mono, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </dt>
      <dd className={`text-foreground mt-0.5 ${mono ? 'font-mono text-xs' : ''}`}>{children}</dd>
    </div>
  );
}
